import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography
} from '@mui/material';

const AccountForm = ({
  open,
  onClose,
  onSubmit,
  account = null,
  loading = false,
  validationErrors = {},
  onFieldChange
}) => {
  const [formData, setFormData] = useState({
    name: '',
    description: ''
  });

  useEffect(() => {
    if (account) {
      setFormData({
        name: account.name || '',
        description: account.description || ''
      });
    } else {
      setFormData({
        name: '',
        description: ''
      });
    }
  }, [account]);

  const handleChange = (field) => (event) => {
    const value = event.target.value;
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    if (onFieldChange) {
      onFieldChange(field, value);
    }
  };

  const handleSubmit = () => {
    onSubmit(formData);
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          backgroundColor: 'var(--ntb-bg-secondary)',
          borderRadius: '8px',
          boxShadow: '0 8px 32px var(--ntb-shadow-color)'
        }
      }}
    >
      <DialogTitle sx={{ 
        borderBottom: '1px solid var(--ntb-border-color)',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}>
        <Typography variant="h6" sx={{ color: 'var(--ntb-text-primary)', fontWeight: 600 }}>
          {account ? 'Edit Account' : 'Create New Account'}
        </Typography>
      </DialogTitle>
      <DialogContent sx={{ mt: 2 }}>
        <Box sx={{ mt: 2 }}>
          <TextField
            fullWidth
            label="Account Name"
            value={formData.name}
            onChange={handleChange('name')}
            error={!!validationErrors.name}
            helperText={validationErrors.name}
            sx={{ 
              mb: 2,
              '& .MuiOutlinedInput-root': {
                backgroundColor: 'var(--ntb-bg-tertiary)',
                borderRadius: '4px',
                '& fieldset': { borderColor: 'var(--ntb-border-color)' },
                '&:hover fieldset': { borderColor: 'var(--ntb-text-tertiary)' },
                '&.Mui-focused fieldset': { borderColor: 'var(--ntb-accent-primary)' }
              },
              '& .MuiInputLabel-root': {
                color: 'var(--ntb-text-tertiary)',
                '&.Mui-focused': {
                  color: 'var(--ntb-accent-primary)'
                }
              },
              '& .MuiInputBase-input': {
                color: 'var(--ntb-text-primary)'
              }
            }}
          />
          <TextField
            fullWidth
            label="Description"
            value={formData.description}
            onChange={handleChange('description')}
            multiline
            rows={3}
            sx={{ 
              '& .MuiOutlinedInput-root': {
                backgroundColor: 'var(--ntb-bg-tertiary)',
                borderRadius: '4px',
                '& fieldset': { borderColor: 'var(--ntb-border-color)' },
                '&:hover fieldset': { borderColor: 'var(--ntb-text-tertiary)' },
                '&.Mui-focused fieldset': { borderColor: 'var(--ntb-accent-primary)' }
              },
              '& .MuiInputLabel-root': {
                color: 'var(--ntb-text-tertiary)',
                '&.Mui-focused': {
                  color: 'var(--ntb-accent-primary)'
                }
              },
              '& .MuiInputBase-input': {
                color: 'var(--ntb-text-primary)'
              }
            }}
          />
          {account && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" sx={{ color: 'var(--ntb-text-tertiary)' }}>
                Warning: Deleting this account will also delete all associated trade entries. This action cannot be undone.
              </Typography>
            </Box>
          )}
        </Box>
      </DialogContent>
      <DialogActions sx={{ p: 2, borderTop: '1px solid var(--ntb-border-color)' }}>
        <Button 
          onClick={onClose}
          sx={{ 
            color: 'var(--ntb-text-tertiary)',
            '&:hover': { 
              backgroundColor: 'var(--ntb-hover-bg)',
              color: 'var(--ntb-text-primary)'
            },
            textTransform: 'none',
            fontWeight: 500
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading}
          sx={{ 
            backgroundColor: 'var(--ntb-accent-primary)',
            '&:hover': { backgroundColor: 'var(--ntb-accent-secondary)' },
            textTransform: 'none',
            fontWeight: 500
          }}
        >
          {account ? 'Save Changes' : 'Create Account'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AccountForm; 